import { useTheme } from "@/hooks/useTheme";

const industries = [
  {
    icon: "ri-bank-line",
    title: "Financial Services & Lending",
    description: "Increase speed to lead and improve deal flow consistency across your entire pipeline.",
    points: ["Automated loan inquiry capture", "Pre-qualification workflows", "Compliance-ready follow-up sequences"],
    image: "https://public.readdy.ai/ai/img_res/2a7ada1663b9c1ef3406c74fc89f1665.jpg",
  },
  {
    icon: "ri-briefcase-4-line",
    title: "Advisory & Brokerage",
    description: "Maintain pipeline control and eliminate missed opportunities across all client touchpoints.",
    points: ["CRM-integrated lead routing", "Instant advisor assignment", "Automated follow-up sequences"],
    image: "https://readdy.ai/api/search-image?query=high-end%20executive%20advisory%20firm%20environment%20with%20senior%20broker%20in%20premium%20office%20reviewing%20portfolio%20data%20on%20multiple%20screens%2C%20refined%20interior%20with%20dark%20wood%20paneling%20and%20soft%20professional%20lighting%2C%20elegant%20brokerage%20atmosphere%20conveying%20authority%20and%20precision%2C%20clean%20modern%20executive%20space&width=700&height=520&seq=adv_v2&orientation=landscape",
  },
  {
    icon: "ri-building-2-line",
    title: "Construction & Project Delivery",
    description: "Capture every inquiry and streamline job coordination from first contact to project kickoff.",
    points: ["Job inquiry automation", "Contractor dispatch workflows", "Project status notifications"],
    image: "https://readdy.ai/api/search-image?query=professional%20construction%20project%20director%20reviewing%20architectural%20plans%20and%20digital%20project%20management%20dashboard%20in%20modern%20site%20office%2C%20clean%20industrial%20aesthetic%20with%20natural%20materials%2C%20confident%20project%20delivery%20team%20in%20premium%20workspace%20with%20large%20blueprint%20displays%20and%20coordination%20screens&width=700&height=520&seq=con_v2&orientation=landscape",
  },
];

export default function IndustriesSection() {
  const { isDark } = useTheme();

  const bg = isDark ? "#120720" : "#f0e9de";
  const cardBg = isDark ? "#1a0d28" : "white";
  const cardBorder = isDark ? "rgba(255,255,255,0.09)" : "#e4d8c8";
  const headingColor = isDark ? "#e4d0f8" : "#4d316c";
  const bodyColor = isDark ? "rgba(255,255,255,0.58)" : "#7a6b80";
  const labelColor = isDark ? "#a9baab" : "#869b8b";
  const dividerColor = isDark ? "rgba(255,255,255,0.09)" : "#e4d8c8";
  const pointColor = isDark ? "rgba(255,255,255,0.65)" : "#5a5060";
  const checkBg = isDark ? "rgba(169,186,171,0.12)" : "#f0e9de";
  const badgeBg = isDark ? "rgba(26,13,40,0.92)" : "rgba(240,233,222,0.92)";
  const badgeText = isDark ? "#c4aad8" : "#4d316c";

  return (
    <section id="industries" className="py-28 md:py-40 transition-colors duration-300" style={{ background: bg }}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase mb-5" style={{ color: labelColor }}>
            Industries
          </span>
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-semibold leading-tight tracking-tight mb-5" style={{ color: headingColor }}>
            Built for High-Stakes
            <br />
            Operations.
          </h2>
          <p className="text-base md:text-lg leading-relaxed" style={{ color: bodyColor }}>
            Patronum AI is engineered for industries where missing a lead has real financial consequences.
          </p>
        </div>

        {/* Large cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {industries.map((ind) => (
            <div
              key={ind.title}
              className="group flex flex-col rounded-3xl overflow-hidden transition-all duration-400 hover:-translate-y-1 cursor-default"
              style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
            >
              {/* Image */}
              <div className="relative w-full overflow-hidden" style={{ height: "280px" }}>
                <img
                  src={ind.image}
                  alt={ind.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                <div
                  className="absolute top-5 left-5 flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold"
                  style={{ background: badgeBg, color: badgeText, backdropFilter: "blur(8px)" }}
                >
                  <i className={`${ind.icon} text-sm`}></i>
                  {ind.title.split(" ")[0]}
                </div>
              </div>

              {/* Content */}
              <div className="flex flex-col flex-1 p-7">
                <h3 className="font-semibold text-lg mb-3 leading-tight" style={{ color: headingColor }}>
                  {ind.title}
                </h3>
                <p className="text-sm leading-relaxed mb-6" style={{ color: bodyColor }}>
                  {ind.description}
                </p>

                <div className="h-px mb-6" style={{ background: dividerColor }} />

                <ul className="flex flex-col gap-3 mt-auto">
                  {ind.points.map((pt) => (
                    <li key={pt} className="flex items-center gap-3 text-sm" style={{ color: pointColor }}>
                      <div
                        className="w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0"
                        style={{ background: checkBg }}
                      >
                        <i className="ri-check-line text-xs" style={{ color: "#869b8b" }}></i>
                      </div>
                      {pt}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
